// Wallets registry (step 6). Lists DB-registered wallets with their
// env-key health (does the resolved signer's address match the registered
// address?), on-chain balances, and how many strategies use each. Detects
// env signers not yet registered and offers a one-click register prompt.
//
// THE RULE the UI enforces visually: keys never appear here. We show only
// the public address + which env reference holds the key. Registration is
// register-then-bind for the wallet_id convention: create the row, then set
// WALLET_<id>_KEY in .env and restart — health flips to "ok" once bound.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, KeyRound, Plus, Wallet as WalletIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionHeader } from '../components/ui/section-header';
import { StatBadge } from '../components/ui/stat-badge';
import { useCurrentUser, useWallets } from '../hooks/useApi';
import {
  walletsApi,
  type CreateWalletBody,
  type UnregisteredSigner,
  type WalletHealth,
  type WalletKeyEnvRef,
  type WalletView,
} from '../lib/api';
import { cn, shortenAddress } from '../lib/utils';

const HEALTH_BADGE: Record<WalletHealth, { variant: 'positive' | 'negative' | 'warning'; label: string }> = {
  ok: { variant: 'positive', label: 'Key OK' },
  mismatch: { variant: 'negative', label: 'Key MISMATCH' },
  no_signer: { variant: 'warning', label: 'No signer' },
};

const KEY_REF_LABEL: Record<WalletKeyEnvRef, string> = {
  wallet_id: 'WALLET_<id>_KEY',
  legacy_primary: 'HOT_PRIVATE_KEY',
  legacy_secondary: 'HOT_PRIVATE_KEY_2',
};

export default function Wallets() {
  const { data, isLoading, error } = useWallets();
  const { data: me } = useCurrentUser();
  const [openCreate, setOpenCreate] = useState(false);
  const [prefill, setPrefill] = useState<Partial<CreateWalletBody> | null>(null);
  const canWrite = !me || me.role === 'admin';

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (error) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-negative">
        Couldn&apos;t load wallets: {String(error)}
      </div>
    );
  }

  const wallets = data?.wallets ?? [];
  const unregistered = data?.unregistered ?? [];

  return (
    <div className="space-y-6 max-w-6xl">
      <SectionHeader
        title="Wallets"
        subtitle="Registered EOAs. Keys live in .env — only the address + env reference are stored here."
        size="lg"
        actions={
          <Button
            size="sm"
            onClick={() => {
              setPrefill(null);
              setOpenCreate(true);
            }}
            disabled={!canWrite}
          >
            <Plus className="h-3.5 w-3.5" />
            Register wallet
          </Button>
        }
      />

      {/* Registered wallets */}
      <div className="rounded-lg border border-border bg-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {['Label / Address', 'Health', 'Key ref', 'ETH', 'WETH', 'USDC', 'Strategies', ''].map(
                (h, i) => (
                  <th
                    key={h || i}
                    className={cn(
                      'text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3',
                      i >= 3 && i <= 5 ? 'text-right' : 'text-left',
                    )}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {wallets.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  No wallets registered yet.
                </td>
              </tr>
            )}
            {wallets.map((w) => (
              <WalletRow key={w.id} wallet={w} canWrite={canWrite} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Detected-but-unregistered env signers */}
      {unregistered.length > 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning-muted/30 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <KeyRound className="h-4 w-4 text-warning" />
            Detected signers not yet registered
          </div>
          <p className="text-xs text-muted-foreground">
            These keys are loaded from .env but have no wallet row. Register them so
            strategies can be assigned and health can be tracked.
          </p>
          <div className="space-y-2">
            {unregistered.map((u) => (
              <UnregisteredRow
                key={u.address}
                signer={u}
                canWrite={canWrite}
                onRegister={() => {
                  setPrefill({ address: u.address, keyEnvRef: u.suggestedKeyEnvRef });
                  setOpenCreate(true);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {openCreate && (
        <RegisterModal
          prefill={prefill}
          onClose={() => setOpenCreate(false)}
        />
      )}
    </div>
  );
}

/** Copy-to-clipboard + Basescan link for a wallet address. Public address
 *  only — keys never surface here. */
function AddressActions({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      <button
        type="button"
        title={copied ? 'Copied!' : 'Copy address'}
        aria-label="Copy address"
        className="text-muted-foreground hover:text-foreground transition-colors"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {
            /* clipboard unavailable (non-secure context) — no-op */
          }
        }}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-positive" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
      <a
        href={`https://basescan.org/address/${address}`}
        target="_blank"
        rel="noopener noreferrer"
        title="View on Basescan"
        aria-label="View on Basescan"
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </span>
  );
}

function WalletRow({ wallet, canWrite }: { wallet: WalletView; canWrite: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const badge = HEALTH_BADGE[wallet.health];

  async function toggleEnabled() {
    setBusy(true);
    try {
      await walletsApi.update(wallet.id, { enabled: !wallet.enabled });
      await qc.invalidateQueries({ queryKey: ['wallets'] });
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/20">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <WalletIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate">{wallet.label}</div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
              <span title={wallet.address}>
                {shortenAddress(wallet.address, 6)}
                {wallet.health === 'mismatch' && wallet.resolvedAddress && (
                  <span className="text-negative">
                    {' '}
                    (env→{shortenAddress(wallet.resolvedAddress, 4)})
                  </span>
                )}
              </span>
              <AddressActions address={wallet.address} />
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatBadge label={badge.label} variant={badge.variant} dot />
        {!wallet.enabled && (
          <StatBadge label="disabled" variant="neutral" className="ml-1" />
        )}
      </td>
      <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
        {wallet.keyEnvRef === 'wallet_id'
          ? `WALLET_${wallet.id}_KEY`
          : KEY_REF_LABEL[wallet.keyEnvRef]}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {wallet.balances ? Number(wallet.balances.eth).toFixed(4) : '—'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {wallet.balances ? Number(wallet.balances.weth).toFixed(4) : '—'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {wallet.balances ? Number(wallet.balances.usdc).toFixed(2) : '—'}
      </td>
      <td className="px-4 py-3 text-left tabular-nums">{wallet.strategyCount}</td>
      <td className="px-4 py-3 text-right">
        <Button
          size="sm"
          variant="secondary"
          disabled={!canWrite || busy}
          onClick={toggleEnabled}
        >
          {wallet.enabled ? 'Disable' : 'Enable'}
        </Button>
      </td>
    </tr>
  );
}

function UnregisteredRow({
  signer,
  canWrite,
  onRegister,
}: {
  signer: UnregisteredSigner;
  canWrite: boolean;
  onRegister: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 font-mono text-xs text-foreground">
          <span title={signer.address}>{shortenAddress(signer.address, 8)}</span>
          <AddressActions address={signer.address} />
        </div>
        <div className="text-xs text-muted-foreground">
          {signer.suggestedKeyEnvRef === 'wallet_id'
            ? `WALLET_${signer.suggestedWalletId}_KEY`
            : KEY_REF_LABEL[signer.suggestedKeyEnvRef]}
        </div>
      </div>
      <Button size="sm" variant="secondary" disabled={!canWrite} onClick={onRegister}>
        Register
      </Button>
    </div>
  );
}

function RegisterModal({
  prefill,
  onClose,
}: {
  prefill: Partial<CreateWalletBody> | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState(prefill?.label ?? '');
  const [address, setAddress] = useState(prefill?.address ?? '');
  const [keyEnvRef, setKeyEnvRef] = useState<WalletKeyEnvRef>(
    prefill?.keyEnvRef ?? 'wallet_id',
  );
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nextStep, setNextStep] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const res = await walletsApi.create({
        label: label.trim(),
        address: address.trim(),
        keyEnvRef,
        notes: notes.trim() || null,
      });
      await qc.invalidateQueries({ queryKey: ['wallets'] });
      if (res.nextStep) {
        // Keep the modal open to show the env-binding instruction.
        setNextStep(res.nextStep);
      } else {
        onClose();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-foreground">Register wallet</h2>

        {nextStep ? (
          <div className="space-y-4">
            <div className="rounded-md border border-primary/30 bg-primary/10 p-3 text-sm text-foreground">
              <p className="font-medium mb-1">Registered. Next step:</p>
              <p className="text-muted-foreground">{nextStep}</p>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              The private key stays in <span className="font-mono">.env</span>. This only
              records the public address and which env reference holds the key.
            </p>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Label</span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Wallet 2 (ETH ±3%)"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                Base EOA address
              </span>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="0x…"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Key reference</span>
              <select
                value={keyEnvRef}
                onChange={(e) => setKeyEnvRef(e.target.value as WalletKeyEnvRef)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="wallet_id">WALLET_&lt;id&gt;_KEY (new convention)</option>
                <option value="legacy_primary">HOT_PRIVATE_KEY (primary)</option>
                <option value="legacy_secondary">HOT_PRIVATE_KEY_2 (secondary)</option>
              </select>
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Notes (optional)</span>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            {err && <p className="text-xs text-negative">{err}</p>}

            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || !label.trim() || !/^0x[a-fA-F0-9]{40}$/.test(address.trim())}
              >
                {busy ? 'Registering…' : 'Register'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
